import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { api, getErrorMessage } from '@/utils';
import { API_ENDPOINTS, buildApiUrl, QUERY_CONFIG } from '@/constants';
import type { GlobalPromptQuestion } from '@/components/globalPrompts/types';

interface GlobalPromptQuestionsResponse {
    questions: GlobalPromptQuestion[];
}

interface UseFetchGlobalPromptQuestionsResult {
    questions: GlobalPromptQuestion[];
    isLoading: boolean;
    isError: boolean;
    errorMessage: string | null;
    refetch: () => void;
}

export const GLOBAL_PROMPT_QUESTIONS_QUERY_KEY = ['api', 'global-prompts', 'questions'] as const;

const useFetchGlobalPromptQuestions = (): UseFetchGlobalPromptQuestionsResult => {
    const { data, isLoading, isError, error, refetch } = useQuery<GlobalPromptQuestionsResponse, AxiosError>({
        queryKey: GLOBAL_PROMPT_QUESTIONS_QUERY_KEY,
        queryFn: async () => {
            const url = buildApiUrl(API_ENDPOINTS.GLOBAL_PROMPT_QUESTIONS);
            const response = await api.get(url);

            return response.data as GlobalPromptQuestionsResponse;
        },
        staleTime: QUERY_CONFIG.STALE_TIME,
        refetchOnWindowFocus: QUERY_CONFIG.REFETCH_ON_WINDOW_FOCUS,
        retry: 2,
    });

    return {
        questions: data?.questions ?? [],
        isLoading,
        isError,
        errorMessage: getErrorMessage(error),
        refetch,
    };
};

export default useFetchGlobalPromptQuestions;
